/* The lifecycle board.

   Drag to advance. The point is not novelty -- it is that changing a stage
   should cost one gesture in the weekly review, because a stage that takes
   four clicks to change is a stage that stays wrong until somebody does a
   tidy-up pass.

   HTML5 drag and drop rather than a library: two event handlers and no
   dependency, and the pointer-precision problems that make drag libraries
   worth their weight do not arise with six large drop targets. */
import { useState } from "react";

import { api, plural, type TrackedObject } from "../api";
import { Bar, Chip, priorityTone, Score } from "../components/ui";
import { href } from "../route";
import { useStore } from "../store";

export default function Board() {
  const { meta, services, assets, run } = useStore();
  /* Services by default. The board is read as a narrative of what is being
     brought to market, and assets -- methodologies, partnerships, delivery
     models -- move on their own rhythm and crowd the Idea column. The filter
     brings them back. */
  const [show, setShow] = useState<"all" | "services" | "assets">("services");
  const [dragging, setDragging] = useState<TrackedObject | null>(null);
  const [over, setOver] = useState<string | null>(null);

  const stages = meta?.lifecycle_stages ?? [];
  const pool =
    show === "services" ? services : show === "assets" ? assets : [...services, ...assets];

  const drop = (stage: string) => {
    setOver(null);
    const object = dragging;
    setDragging(null);
    if (!object || object.lifecycle_stage === stage) return;
    void run(() => api.patch(plural(object.kind), object.id, { lifecycle_stage: stage }));
  };

  return (
    <>
      <div className="filters">
        <label>
          Show
          <select value={show} onChange={(e) => setShow(e.target.value as typeof show)}>
            <option value="all">Services and assets</option>
            <option value="services">Services only</option>
            <option value="assets">Assets only</option>
          </select>
        </label>
        <span className="count">drag a card to change its stage</span>
      </div>

      <div className="board">
        {stages.map((stage) => {
          const cards = pool.filter((o) => o.lifecycle_stage === stage);
          return (
            <div
              key={stage}
              className={`col${over === stage ? " over" : ""}`}
              onDragOver={(e) => {
                e.preventDefault();
                setOver(stage);
              }}
              onDragLeave={() => setOver((s) => (s === stage ? null : s))}
              onDrop={() => drop(stage)}
            >
              <h3>
                <span>{stage}</span>
                <span className="muted">{cards.length}</span>
              </h3>
              <div>
                {cards.map((o) => (
                  <article
                    key={`${o.kind}-${o.id}`}
                    className={`ocard ${o.priority === "High" ? "high" : ""} ${o.kind}`}
                    draggable
                    onDragStart={() => setDragging(o)}
                    onDragEnd={() => setDragging(null)}
                  >
                    <h4>
                      <a href={href.object(o.kind, o.id)} style={{ color: "inherit", textDecoration: "none" }}>
                        {o.name}
                      </a>
                    </h4>
                    <div className="meta">
                      <span>{o.owner_name ?? "Unassigned"}</span>
                      <Chip text={o.priority} tone={priorityTone(o.priority)} />
                    </div>
                    <div className="meta" style={{ marginTop: 4 }}>
                      <span>{o.class}</span>
                      <span className="num">
                        <Score value={o.maturity.score} />
                      </span>
                    </div>
                    <Bar value={o.maturity.score} />
                  </article>
                ))}
                {cards.length === 0 && (
                  <p className="muted" style={{ fontSize: 12, margin: "4px 2px" }}>
                    nothing here
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <p className="note" style={{ marginTop: 14 }}>
        Assets are drawn with a dashed edge. Dependencies are recorded but never enforced —
        moving a service to Launched while something it requires is still an Idea is allowed,
        and shows up on the object's page rather than as a block here.
      </p>
    </>
  );
}
